import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Sparkles, RefreshCw, Siren, History, Settings as SettingsIcon, Target, Users, BarChart3,
    ShieldAlert, Wand2, Database,
} from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { SALES_AGENT_OVERVIEW_URL, SALES_AGENT_GENERATE_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { formatINR, STAGE_COLORS } from "../../components/dashboard/shared";
import BriefingView from "./BriefingView";
import {
    PriorityChip, Freshness, ScopePicker, EmptyState, formatMetric, useSalesAgentAccess,
} from "./shared";

/**
 * Sales Manager Intelligence -> Overview (trustedge sales_agent/).
 *
 * Every figure on this page is a database count with a written definition
 * (hover a card). The briefing on top is either rule-based or written by the
 * local AI worker and checked against these figures; the page works the same
 * when the AI has not run.
 */

const PERIODS = [
    ["today", "Today", () => [dayjs(), dayjs()]],
    ["yesterday", "Yesterday", () => [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")]],
    ["7d", "Last 7 days", () => [dayjs().subtract(6, "day"), dayjs()]],
    ["month", "This month", () => [dayjs().startOf("month"), dayjs()]],
    ["30d", "Last 30 days", () => [dayjs().subtract(29, "day"), dayjs()]],
];

const GROUPS = [
    ["Leads", ["new_leads", "contacted", "contact_rate", "first_response_hours", "unassigned_open"]],
    ["Activity", ["activities", "calls", "demos_scheduled", "demos_done", "proposals_sent", "followups_today", "overdue_followups", "next_action_coverage"]],
    ["Outcomes", ["won", "lost", "win_rate", "revenue_won", "avg_deal_value", "pipeline_value", "weighted_pipeline"]],
];
const WATCH = new Set(["overdue_followups", "unassigned_open"]);
const QUALITY = ["missing_contact", "missing_source", "missing_next_action", "missing_lost_reason", "stale_records"];

function Kpi({ k, metric, def }) {
    const bad = WATCH.has(k) && metric?.value > 0;
    return (
        <div title={`${def?.formula || ""}${metric?.note ? "\n" + metric.note : ""}`}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
            <p className="truncate text-[10px] font-medium uppercase tracking-wide text-gray-400">{def?.label || k}</p>
            <p className={cn("text-lg font-semibold tabular-nums", bad ? "text-rose-600 dark:text-rose-400" : "text-gray-900 dark:text-white")}>
                {formatMetric(metric, def?.unit)}
            </p>
            {metric?.value == null && metric?.note && <p className="truncate text-[10px] text-gray-400">{metric.note}</p>}
            {metric?.value != null && def?.unit === "pct" && metric.denominator != null && (
                <p className="text-[10px] text-gray-400 tabular-nums">{metric.numerator} of {metric.denominator}</p>
            )}
        </div>
    );
}

function Card({ icon: Icon, title, action, children, className }) {
    return (
        <section className={cn("rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60", className)}>
            <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <Icon className="h-4 w-4 text-primary" /> {title}
                </h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function Targets({ targets }) {
    if (!targets?.available) {
        return <EmptyState icon={Target} title="No targets this month">{targets?.note}</EmptyState>;
    }
    const lines = [["revenue", "Revenue", formatINR], ["closures", "Closures", v => v], ["demos", "Demos done", v => v]];
    return (
        <div className="space-y-3">
            {lines.map(([key, label, fmt]) => {
                const l = targets.lines[key];
                if (!l || !l.target) return null;
                const pct = Math.min(100, l.pct || 0);
                const behind = l.pct != null && l.pct < targets.pace_pct;
                return (
                    <div key={key}>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-300">{label}</span>
                            <span className="tabular-nums text-gray-500">{fmt(l.achieved)} / {fmt(l.target)} · {l.pct ?? 0}%</span>
                        </div>
                        <div className="relative mt-1 h-2 rounded bg-gray-100 dark:bg-gray-700">
                            <div className={cn("h-2 rounded", behind ? "bg-amber-500" : "bg-emerald-500")} style={{ width: `${pct}%` }} />
                            <div className="absolute top-[-2px] h-3 w-0.5 bg-gray-500" style={{ left: `${Math.min(100, targets.pace_pct)}%` }}
                                title={`${targets.pace_pct}% of the month gone`} />
                        </div>
                    </div>
                );
            })}
            <p className="text-[11px] text-gray-400">The tick is today's pace: {targets.pace_pct}% of the month gone. {targets.people} people with targets.</p>
        </div>
    );
}

export default function SalesAgentOverview() {
    const { fetch, create } = useApiMethods();
    const navigate = useNavigate();
    const access = useSalesAgentAccess();
    const [period, setPeriod] = useState("today");
    const [scope, setScope] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(() => {
        const [s, e] = PERIODS.find(p => p[0] === period)[2]();
        const query = { start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD") };
        if (scope) query.scope = scope;
        setLoading(true);
        fetch(SALES_AGENT_OVERVIEW_URL, query)
            .then(res => {
                if (res?.status === "success") { setData(res.data); setError(null); }
                else setError(res?.message || "Could not load the overview");
            })
            .finally(() => setLoading(false));
    }, [period, scope]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // A queued briefing gets its AI narrative from the local worker; look again shortly.
    useEffect(() => {
        if (!["queued", "running"].includes(data?.briefing?.status)) return undefined;
        const t = setTimeout(load, 20000);
        return () => clearTimeout(t);
    }, [data, load]);

    const generate = async () => {
        setGenerating(true);
        try {
            const res = await create(SALES_AGENT_GENERATE_URL, { scope: scope || data?.scope?.key });
            if (res?.status === "success") {
                ToastMsg("success", "Briefing updated. The AI narrative is added when the AI worker runs.");
                load();
            } else ToastMsg("error", res?.message || "Could not generate the briefing");
        } finally { setGenerating(false); }
    };

    const defs = data?.definitions || {};
    return (
        <ContainerCard>
            <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                            <Sparkles className="h-4 w-4 text-primary" /> Sales Manager Intelligence
                        </h1>
                        {data && <Freshness calculatedAt={data.calculated_at} rulesAt={data.rules_last_evaluated}
                            extra={<span>{data.scope.label}</span>} />}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <ScopePicker scopes={data?.scopes} value={scope || data?.scope?.key} onChange={setScope} />
                        <select value={period} onChange={e => setPeriod(e.target.value)} aria-label="Reporting period"
                            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                            {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={load} disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => navigate("/reports/action-center")}>
                            <Siren className="h-3 w-3" /> Action Center
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => navigate("/sales-agent/briefings")}>
                            <History className="h-3 w-3" /> Briefings
                        </Button>
                        {access.canSettings && (
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Settings" onClick={() => navigate("/sales-agent/settings")}>
                                <SettingsIcon className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {error && !data && <EmptyState icon={ShieldAlert} title="Could not load">{error}</EmptyState>}
                {!data && loading && (
                    <div className="space-y-3">
                        <Skeleton className="h-40 w-full" />
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
                    </div>
                )}

                {data && (
                    <>
                        <div className="grid gap-4 lg:grid-cols-3">
                            <Card icon={Sparkles} title="Daily briefing" className="lg:col-span-2"
                                action={data.can_act && (
                                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={generate} disabled={generating}>
                                        <Wand2 className="h-3 w-3" /> {data.briefing ? "Regenerate" : "Generate now"}
                                    </Button>
                                )}>
                                {data.briefing ? <BriefingView run={data.briefing} /> : (
                                    <EmptyState icon={Sparkles} title="No briefing yet">
                                        The daily briefing is written at the time set in Settings on working days.
                                        {data.can_act && " Use Generate now for one straight away."}
                                    </EmptyState>
                                )}
                            </Card>
                            <Card icon={Target} title={`Targets — ${dayjs(data.targets?.month).format("MMMM")}`}>
                                <Targets targets={data.targets} />
                            </Card>
                        </div>

                        {GROUPS.map(([label, keys]) => (
                            <div key={label}>
                                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                                    {keys.map(k => <Kpi key={k} k={k} metric={data.kpis[k]} def={defs[k]} />)}
                                </div>
                            </div>
                        ))}

                        <div className="grid gap-4 lg:grid-cols-2">
                            <Card icon={Siren} title={`Needs attention (${data.alerts.open} open)`}
                                action={<Link to="/reports/action-center" className="text-xs text-primary hover:underline">All alerts</Link>}>
                                <div className="mb-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
                                    {Object.entries(data.alerts.by_priority).map(([p, n]) => (
                                        <span key={p} className="inline-flex items-center gap-1"><PriorityChip priority={p} /> {n}</span>
                                    ))}
                                    <span>· {data.alerts.auto_cleared_7d} cleared by the team in the last 7 days</span>
                                </div>
                                {data.alerts.top.length === 0 ? (
                                    <EmptyState title="Nothing urgent">No open alerts in this view.</EmptyState>
                                ) : (
                                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {data.alerts.top.map(a => (
                                            <li key={a.id} className="flex items-start gap-2 py-2">
                                                <PriorityChip priority={a.priority} score={a.priority_score} />
                                                <button type="button" className="min-w-0 flex-1 text-left"
                                                    onClick={() => navigate(`/reports/action-center?alert=${a.id}`)}>
                                                    <p className="truncate text-sm text-gray-900 hover:text-primary dark:text-white">{a.title}</p>
                                                    <p className="truncate text-xs text-gray-500">{a.recommended_action}</p>
                                                </button>
                                                <span className="shrink-0 text-[11px] text-gray-400">{a.owner || a.person || "Managers"}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>

                            <Card icon={BarChart3} title="Pipeline by stage (open leads, now)">
                                {data.pipeline.length === 0 ? <EmptyState title="No open leads" /> : (
                                    <table className="w-full text-xs">
                                        <thead><tr className="text-left text-[10px] uppercase text-gray-400">
                                            <th className="py-1 font-medium">Stage</th><th className="font-medium text-right">Leads</th>
                                            <th className="font-medium text-right">Value</th><th className="font-medium text-right">Probability</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.pipeline.map(r => (
                                                <tr key={r.stage} className="border-t border-gray-100 dark:border-gray-700">
                                                    <td className="py-1.5"><span className={cn("mr-2 inline-block h-2 w-2 rounded-full", STAGE_COLORS[r.stage] || "bg-gray-400")} />{r.stage}</td>
                                                    <td className="text-right tabular-nums">{r.count}</td>
                                                    <td className="text-right tabular-nums">{formatINR(r.value)}</td>
                                                    <td className="text-right tabular-nums text-gray-500">{r.probability != null ? `${r.probability}%` : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <p className="mt-2 text-[11px] text-gray-400">Weighted pipeline uses the probabilities in Settings → Forecast Config. It is an estimate, not expected revenue.</p>
                            </Card>
                        </div>

                        {data.team.length > 0 && (
                            <Card icon={Users} title="Team (activity in the selected period; leads and backlog as of now)">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[36rem] text-xs">
                                        <thead><tr className="text-left text-[10px] uppercase text-gray-400">
                                            <th className="py-1 font-medium">Salesperson</th>
                                            <th className="text-right font-medium">Open leads</th>
                                            <th className="text-right font-medium">Overdue</th>
                                            <th className="text-right font-medium">Activities</th>
                                            <th className="text-right font-medium">Demos done</th>
                                            <th className="text-right font-medium">Won</th>
                                            <th className="text-right font-medium">Revenue</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.team.map(r => (
                                                <tr key={r.user_id} className="border-t border-gray-100 dark:border-gray-700">
                                                    <td className="py-1.5">
                                                        <button type="button" className="hover:text-primary" onClick={() => setScope(`user:${r.user_id}`)}>
                                                            {r.name}
                                                        </button>
                                                        {!r.is_active && <span className="ml-1 text-[10px] text-rose-500">inactive</span>}
                                                    </td>
                                                    <td className="text-right tabular-nums">{r.open_leads}</td>
                                                    <td className={cn("text-right tabular-nums", r.overdue_followups > 0 && "text-rose-600")}>{r.overdue_followups}</td>
                                                    <td className="text-right tabular-nums">{r.activities}</td>
                                                    <td className="text-right tabular-nums">{r.demos_done}</td>
                                                    <td className="text-right tabular-nums">{r.won}</td>
                                                    <td className="text-right tabular-nums">{formatINR(r.revenue_won)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="mt-2 text-[11px] text-gray-400">Activity is credited to whoever logged it. Compare like with like: lead volume and sources differ between people.</p>
                            </Card>
                        )}

                        <Card icon={Database} title="CRM data quality (open leads, now)">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                                {QUALITY.map(k => <Kpi key={k} k={k} metric={data.kpis[k]} def={defs[k]} />)}
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}

